import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

/**
 * Preładowanie lazy-chunków świadome łącza.
 *
 * PreloadAllModules dociąga WSZYSTKIE trasy natychmiast po pierwszej nawigacji —
 * na wolnym/metered łączu mobilnym ten burst konkuruje z pierwszym fetchem danych
 * dashboardu (dokładnie ten scenariusz, który powodował "freeze" na iPhone).
 *
 * Ta strategia:
 *  - całkowicie pomija preloading, gdy użytkownik ma Data Saver albo łącze 2g/slow-2g,
 *  - w pozostałych przypadkach opóźnia preload o krótki bufor, by nie ścigać się
 *    z requestami danych pierwszego widoku,
 *  - pozwala per-trasa wymusić natychmiastowy preload przez `data: { preload: true }`
 *    lub całkiem go wyłączyć przez `data: { preload: false }`.
 */
const PRELOAD_DELAY_MS = 2000;

interface NetworkInformationLike {
  saveData?: boolean;
  effectiveType?: string;
}

@Injectable({ providedIn: 'root' })
export class NetworkAwarePreloadingStrategy implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    const explicit = route.data?.['preload'];
    if (explicit === false) {
      return of(null);
    }
    if (explicit === true) {
      return load();
    }

    if (this.isConstrainedNetwork()) {
      // Na słabym/oszczędnym łączu nie preloadujemy — chunk dociągnie się
      // przy realnym wejściu w zakładkę (pasek postępu daje feedback).
      return of(null);
    }

    return timer(PRELOAD_DELAY_MS).pipe(mergeMap(() => load()));
  }

  private isConstrainedNetwork(): boolean {
    const connection = (navigator as Navigator & {
      connection?: NetworkInformationLike;
    }).connection;

    if (!connection) {
      return false; // brak API (Safari/iOS) — zakładamy normalne łącze
    }
    if (connection.saveData) {
      return true;
    }
    const type = connection.effectiveType;
    return type === 'slow-2g' || type === '2g';
  }
}
